import React, { useState } from 'react';
import axios from 'axios';
import { Languages, Loader2 } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import useAiFeatureStatus from '../../hooks/useAiFeatureStatus';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Most common target languages for a global dog-owner audience.
const LANGS = [
  { code: 'Spanish', label: 'Español (Spanish)' },
  { code: 'French', label: 'Français (French)' },
  { code: 'German', label: 'Deutsch (German)' },
  { code: 'Portuguese', label: 'Português (Portuguese)' },
  { code: 'Italian', label: 'Italiano (Italian)' },
  { code: 'Japanese', label: '日本語 (Japanese)' },
  { code: 'Chinese', label: '中文 (Chinese)' },
  { code: 'Korean', label: '한국어 (Korean)' },
  { code: 'Hindi', label: 'हिन्दी (Hindi)' },
  { code: 'Arabic', label: 'العربية (Arabic)' },
  { code: 'English', label: 'English' },
];

function detectBrowserLang() {
  try {
    const l = (navigator.language || 'en').split('-')[0].toLowerCase();
    const map = {
      es: 'Spanish', fr: 'French', de: 'German', pt: 'Portuguese', it: 'Italian',
      ja: 'Japanese', zh: 'Chinese', ko: 'Korean', hi: 'Hindi', ar: 'Arabic',
      en: 'Spanish', // English speakers are offered Spanish by default (most common non-English on our platform)
    };
    return map[l] || 'Spanish';
  } catch {
    return 'Spanish';
  }
}

/**
 * TranslatePopover — globe-icon button that opens a language picker and
 * translates the caller-supplied text via POST /api/ai/translate
 * (Claude Sonnet 4.5). Results are cached per-language in local state so
 * toggling show/hide doesn't re-hit the API.
 *
 * Props:
 *  - title: string (post title)
 *  - body: string (post body / content)
 *  - onTranslated(result | null): parent callback; receives
 *      { lang, title, body } on success, null when the user clicks "Show original".
 *  - compact: boolean — small button when true (used inside action bars)
 *  - testId: string — data-testid stable prefix
 */
export default function TranslatePopover({ title, body, onTranslated, compact = true, testId = 'translate' }) {
  const { token } = useAuth();
  const { flags: aiFlags } = useAiFeatureStatus();
  const [open, setOpen] = useState(false);
  const [targetLang, setTargetLang] = useState(detectBrowserLang());
  const [loading, setLoading] = useState(false);
  const [cache, setCache] = useState({}); // { [lang]: { title, body } }
  const [activeLang, setActiveLang] = useState(null);

  // Hide the button entirely when the AI translation flag is off.
  // Keeps untested feature surfaces invisible to non-admins.
  if (!aiFlags?.ai_translation) return null;

  const translate = async () => {
    // Serve from cache if we've already translated this text into this lang.
    if (cache[targetLang]) {
      setActiveLang(targetLang);
      onTranslated?.({ lang: targetLang, ...cache[targetLang] });
      setOpen(false);
      return;
    }

    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const payload = `${(title || '').trim()}\n\n${(body || '').trim()}`.trim();
      const res = await axios.post(
        `${API}/ai/translate`,
        { text: payload, target_lang: targetLang },
        { headers, withCredentials: true }
      );
      const translated = res.data?.translation || '';
      // Split the result back into title/body on the first blank-line boundary
      // (mirrors the way we concatenated the source above).
      let tTitle = '';
      let tBody = translated;
      const splitIdx = translated.indexOf('\n\n');
      if (splitIdx > -1 && splitIdx < 200) {
        tTitle = translated.slice(0, splitIdx).trim();
        tBody = translated.slice(splitIdx + 2).trim();
      }
      const entry = { title: tTitle, body: tBody };
      setCache((prev) => ({ ...prev, [targetLang]: entry }));
      setActiveLang(targetLang);
      onTranslated?.({ lang: targetLang, ...entry });
      setOpen(false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503) {
        toast.error('Translation is turned off right now.');
      } else if (status === 401) {
        toast.error('Please log in to translate.');
      } else {
        toast.error('Translation failed — try again in a moment.');
      }
    } finally {
      setLoading(false);
    }
  };

  const showOriginal = (e) => {
    e.stopPropagation();
    setActiveLang(null);
    onTranslated?.(null);
  };

  if (activeLang) {
    return (
      <button
        type="button"
        className={`flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors ${compact ? 'text-sm' : ''}`}
        onClick={showOriginal}
        data-testid={`${testId}-show-original`}
      >
        <Languages className="w-4 h-4" />
        <span>Show original</span>
      </button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors ${compact ? 'text-sm' : ''}`}
          onClick={(e) => e.stopPropagation()}
          data-testid={`${testId}-btn`}
          aria-label="Translate"
        >
          <Languages className="w-4 h-4" />
          <span className="hidden sm:inline">Translate</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-3"
        align="start"
        onClick={(e) => e.stopPropagation()}
        data-testid={`${testId}-popover`}
      >
        <div className="space-y-2">
          <div className="text-xs font-medium text-muted-foreground">Translate to</div>
          <Select value={targetLang} onValueChange={setTargetLang}>
            <SelectTrigger data-testid={`${testId}-lang-select`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGS.map((l) => (
                <SelectItem key={l.code} value={l.code} data-testid={`${testId}-lang-${l.code}`}>
                  {l.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            className="w-full"
            size="sm"
            onClick={translate}
            disabled={loading || !(title || body)}
            data-testid={`${testId}-submit`}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" /> Translating…
              </>
            ) : (
              <>
                <Languages className="w-4 h-4 mr-1" /> Translate
              </>
            )}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
